import { useCallback, useEffect, useState } from 'react';

import {
  IDLE_TIMER,
  ITimerState,
  pauseTimer,
  readDrafts,
  startTimer,
  TDrafts,
  writeDrafts,
} from './presentationDrafts';
import { TPresentationScores } from './presentationRules';

export interface IPresentationDrafts {
  /** Rascunho da equipe, se houver; senão `saved` (as notas da API). */
  scoresOf: (teamId: string, saved: TPresentationScores) => TPresentationScores;
  hasDraft: (teamId: string) => boolean;
  setScores: (teamId: string, scores: TPresentationScores) => void;
  /** Depois de salvar: as notas passam a vir da API. O cronômetro fica. */
  clearScores: (teamId: string) => void;
  timerOf: (teamId: string) => ITimerState;
  start: (teamId: string) => void;
  pause: (teamId: string) => void;
  reset: (teamId: string) => void;
}

/**
 * Rascunho e cronômetro da banca por equipe, guardados no `sessionStorage`
 * do torneio. A aba Apresentações e a tela antiga da banca usam o mesmo, então
 * mostram as mesmas notas e o mesmo tempo.
 */
export default function usePresentationDrafts(
  tournamentId: string,
): IPresentationDrafts {
  const [drafts, setDrafts] = useState<TDrafts>(() => readDrafts(tournamentId));

  useEffect(() => {
    setDrafts(readDrafts(tournamentId));
  }, [tournamentId]);

  const update = useCallback(
    (teamId: string, change: (draft: TDrafts[string]) => TDrafts[string]) =>
      setDrafts(prev => {
        const next = { ...prev, [teamId]: change(prev[teamId] ?? {}) };
        writeDrafts(tournamentId, next);
        return next;
      }),
    [tournamentId],
  );

  const timerOf = useCallback(
    (teamId: string) => drafts[teamId]?.timer ?? IDLE_TIMER,
    [drafts],
  );

  return {
    scoresOf: (teamId, saved) => drafts[teamId]?.scores ?? saved,
    hasDraft: teamId => Boolean(drafts[teamId]?.scores),
    setScores: (teamId, scores) => update(teamId, d => ({ ...d, scores })),
    clearScores: teamId =>
      update(teamId, ({ scores: _scores, ...rest }) => rest),
    timerOf,
    start: teamId =>
      update(teamId, d => ({
        ...d,
        timer: startTimer(d.timer ?? IDLE_TIMER, Date.now()),
      })),
    pause: teamId =>
      update(teamId, d => ({
        ...d,
        timer: pauseTimer(d.timer ?? IDLE_TIMER, Date.now()),
      })),
    reset: teamId => update(teamId, d => ({ ...d, timer: IDLE_TIMER })),
  };
}
